<template>
  <div class="answer-element">
    <div class="answer-header">
      <span class="answer-type-badge">
        <i class="fas fa-reply"></i>
        <span>Réponse utilisateur</span>
      </span>
      <div class="element-actions">
        <button class="action-btn" @click.stop="$emit('duplicate')" title="Dupliquer">
          <i class="fas fa-copy"></i>
        </button>
        <button class="action-btn delete-btn" @click.stop="$emit('remove')" title="Supprimer">
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>
    
    <div class="answer-content">
      <div class="form-group">
        <label>Types de réponse</label>
        <vue-multiselect
          v-model="selectedTypes"
          :options="answerTypes"
          :multiple="true"
          :close-on-select="false"
          :clear-on-select="false"
          :preserve-search="true"
          placeholder="Sélectionner les types de réponse"
          :preselect-first="false"
          @input="handleTypeChange"
          @select="handleTypeChange"
          @remove="handleTypeChange"
        >
          <template v-slot:option="{ option }">
            <span>{{ getTypeLabel(option) }}</span>
          </template>
          <template v-slot:tag="{ option }">
            <span class="multiselect-tag">{{ getTypeLabel(option) }}</span>
          </template>
        </vue-multiselect>
      </div>
      
      <div class="form-group">
        <label>Variable</label>
        <div class="select-wrapper">
          <i class="fas fa-code tag-icon"></i>
          <select 
            v-model="selectedVariable" 
            class="variable-select"
            @change="handleVariableChange"
          >
            <option value="" disabled>Sélectionnez une variable</option>
            <option 
              v-for="variable in variables" 
              :key="variable.id" 
              :value="variable.id"
            >
              {{variable.id}}
            </option>
          </select>
        </div>
      </div>
      
      <div class="form-group">
        <label>Réponse par défaut</label>
        <div class="input-wrapper">
          <i class="fas fa-exclamation-triangle tag-icon"></i>
          <input 
            type="text" 
            v-model="defaultReply"
            class="default-reply-input"
            placeholder="Réponse par défaut en cas d'erreur"
            @input="handleDefaultReplyChange"
          >
        </div>
      </div>
      
      <div class="answer-description">
        Configure la façon dont l'utilisateur peut répondre
      </div>
    </div>
  </div>
</template>

<script>
import VueMultiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'

export default {
  name: 'AnswerElement',
  components: {
    VueMultiselect
  },
  props: {
    element: {
      type: Object,
      required: true
    },
    variables: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedTypes: this.getInitialTypes(),
      selectedVariable: this.getInitialVariable(),
      defaultReply: this.getInitialDefaultReply(),
      defaultReplyTimeout: null,
      answerTypes: [
        'text',
        'interactive',
        'audio',
        'tel_sen',
        'phone_number',
        'alphabet',
        'email',
        'number',
        'location'
      ]
    }
  },
  methods: {
    getInitialTypes() {
      return this.element.options?.answer_type || [];
    },
    getTypeLabel(type) {
      const labels = {
        'text': 'Texte',
        'interactive': 'Interactif',
        'audio': 'Audio',
        'tel_sen': 'Téléphone sénégalais',
        'phone_number': 'Numéro de téléphone',
        'alphabet': 'Alphabétique',
        'email': 'Email',
        'number': 'Nombre',
        'location': 'Localisation'
      };
      return labels[type] || type;
    },
    getInitialVariable() {
      return this.element.options?.variable?.id || '';
    },
    getInitialDefaultReply() {
      return this.element.options?.default_reply || '';
    },
    handleTypeChange() {
      this.$emit('update', {
        ...this.element,
        options: {
          ...this.element.options,
          answer_type: this.selectedTypes
        }
      });
    },
    handleVariableChange() {
      this.$emit('update', {
        ...this.element,
        options: {
          ...this.element.options,
          variable: { id: this.selectedVariable }
        }
      });
    },
    handleDefaultReplyChange() {
      // Annuler le timeout précédent s'il existe
      if (this.defaultReplyTimeout) {
        clearTimeout(this.defaultReplyTimeout);
      }
      
      // Créer un nouveau timeout de 1.5 seconde
      this.defaultReplyTimeout = setTimeout(() => {
        this.$emit('update', {
          ...this.element,
          options: {
            ...this.element.options,
            default_reply: this.defaultReply
          }
        });
      }, 1500);
    }
  },
  
  beforeUnmount() {
    // Nettoyer le timeout en cas de destruction du composant
    if (this.defaultReplyTimeout) {
      clearTimeout(this.defaultReplyTimeout);
    }
  }
}
</script>

<style>
@import 'vue-multiselect/dist/vue-multiselect.css';
</style>

<style scoped>
.answer-element {
  background: #ffffff;
  border-radius: 16px;
  overflow: visible;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 1;
}

.answer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background: linear-gradient(to right, #ecfdf5, #d1fae5);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.answer-type-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #059669;
  font-weight: 500;
}

.element-actions {
  display: flex;
  gap: 0.75rem;
}

.action-btn {
  background: white;
  border: none;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #64748b;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.action-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.action-btn.delete-btn:hover {
  background: #fee2e2;
  color: #ef4444;
}

.answer-content {
  padding: 1.25rem;
  overflow: visible;
  position: relative;
}

.form-group {
  margin-bottom: 1.25rem;
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
}

.select-wrapper,
.input-wrapper {
  position: relative;
  z-index: 1;
}

.tag-icon {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  z-index: 1;
}

.variable-select,
.default-reply-input {
  width: 100%;
  padding: 0.625rem 1rem 0.625rem 2.25rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  background: white;
  color: #1e293b;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
}

.variable-select:hover,
.default-reply-input:hover {
  border-color: #6ee7b7;
}

.variable-select:focus,
.default-reply-input:focus {
  outline: none;
  border-color: #059669;
  box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
}

.answer-description {
  color: #64748b;
  font-size: 0.875rem;
  margin-top: 0.75rem;
}

.multiselect-tag {
  background: #f1f5f9;
  color: #334155;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  margin: 2px;
  display: inline-block;
}

/* Styles pour le multiselect dropdown */
.form-group {
  position: relative;
  z-index: 10;
}

.form-group:has(.multiselect--active) {
  z-index: 10000 !important;
}

.multiselect {
  z-index: 9999 !important;
  position: relative !important;
}

.multiselect__content-wrapper {
  z-index: 10000 !important;
  position: absolute !important;
  top: 100% !important;
  left: 0 !important;
  right: 0 !important;
}

.multiselect__content {
  z-index: 10001 !important;
  background: white !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

/* S'assurer que les autres form-group ont un z-index inférieur quand le multiselect est actif */
.answer-content:has(.multiselect--active) .form-group:not(:has(.multiselect--active)) {
  z-index: 1 !important;
}

.answer-content:has(.multiselect--active) .select-wrapper,
.answer-content:has(.multiselect--active) .input-wrapper {
  z-index: 1 !important;
}

.answer-content:has(.multiselect--active) .variable-select,
.answer-content:has(.multiselect--active) .default-reply-input {
  z-index: 1 !important;
}

</style>
